import { alpha, Box, Button, Typography, useTheme } from '@mui/material';
import React, { ChangeEvent, DragEvent, useState } from 'react';
import { getPolyglot } from '../../../../../i18n';
import { IconUpload } from '../../../../../redux/groups/groups.model';
import { AddPictureIcon } from '../../../../../theme/icons';

interface ImageUploaderProps {
  initialImage?: string;
  maxWidthImagePixel?: number;
  maxHeightImagePixel?: number;
  maxSizeKByte?: number;
  onImageChange?: (image: IconUpload | null) => void;
}

const MAX_SIZE_IMAGE_KBYTE = 8192;
const KBYTE = 1024;
const MAX_WIDTH_IMAGE = 1200; // 1200px
const MAX_HEIGHT_IMAGE = 1200; // 1200px
export default function ImageUploader(props: ImageUploaderProps) {
  const {
    initialImage,
    maxWidthImagePixel,
    maxHeightImagePixel,
    maxSizeKByte,
    onImageChange,
  } = props;
  const [preview, setPreview] = useState<string | null>(initialImage || null);
  const [error, setError] = useState<string>('');
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const maxWidthImage = maxWidthImagePixel || MAX_WIDTH_IMAGE;
  const maxHeightImage = maxHeightImagePixel || MAX_HEIGHT_IMAGE;
  const maxSize = (maxSizeKByte || MAX_SIZE_IMAGE_KBYTE) * KBYTE;

  const polyglot = getPolyglot();

  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

  const resetError = () => {
    setError('');
    setIsInvalid(false);
  };

  const validateImage = (file: File): string | null => {
    if (!allowedTypes.includes(file.type))
      return polyglot.t('image.invalid_type', {
        types: allowedTypes.join(', '),
      });
    if (file.size > maxSize)
      return polyglot.t('image.invalid_dimensions', {
        size: maxSize / KBYTE,
      });
    return null;
  };

  const handleFile = (file: File) => {
    const validationError = validateImage(file);
    if (validationError) {
      setError(validationError);
      setIsInvalid(true);
      setPreview(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      resetError();
      setPreview(URL.createObjectURL(file));
    };
    img.src = URL.createObjectURL(file);
    setPreview(URL.createObjectURL(file));
    resetError();

    img.onload = () => {
      if (img.width > maxWidthImage || img.height > maxHeightImage) {
        setError(
          `${polyglot.t('image.invalid_dimensions', {
            width: maxWidthImage,
            height: maxHeightImage,
          })}`
        );
        setIsInvalid(true);
        setPreview(null);
      }
      if (!isInvalid) {
        const image = {
          fileName: file.name,

          file: file,
          imagePreviewUrl: img.src,
        };
        onImageChange?.(image);
      }
    };
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    resetError();
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    resetError();

    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const removeImage = () => {
    setPreview(null);
    setError('');
    setIsInvalid(false);
    onImageChange?.(null);
  };

  const theme = useTheme();

  return (
    <Box width="100%" maxWidth={420} mx="auto" mt={2}>
      <Typography textAlign="start" variant="subtitle1" mb={1} fontWeight={600}>
        {polyglot.t('image.title')}
      </Typography>

      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          minHeight: 200,
          border: '2px dashed',
          borderColor: isInvalid
            ? 'error.main'
            : isDragging
            ? 'grey.500'
            : 'primary.main',
          bgcolor: (theme) =>
            isInvalid
              ? alpha(theme.palette.error.main, 0.1)
              : isDragging
              ? 'grey.100'
              : alpha(theme.palette.primary.main, 0.1),
          borderRadius: 3,
          p: '1rem',
          textAlign: 'center',
          position: 'relative',
          transition: '0.2s',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {!preview ? (
          <>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleInputChange}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                pointerEvents: 'auto',
              }}
            />
            <AddPictureIcon height="3rem" color={theme.palette.primary.main} />

            <Typography color="text.secondary">
              {polyglot.t('image.drop_image_here')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {polyglot.t('image.max_size', { size: MAX_SIZE_IMAGE_KBYTE })}
            </Typography>
          </>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <img
              src={preview}
              alt="Preview"
              style={{ height: 160, objectFit: 'contain', borderRadius: 8 }}
            />
            <Button variant="outlined" color="secondary" onClick={removeImage}>
              {polyglot.t('image.remove_image')}
            </Button>
          </Box>
        )}

        {error && (
          <Typography color="error" fontSize={14} mt={2} fontWeight={500}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
